import * as React from 'react';
import EventStepper from '../../../../../components/EventStepper';
import { fm } from 'Components/FormatMessage';
import InputHandler from '../../../../../components/InputHandler';
import colors from '../../../../../config/theme/colors';
import { formatPartialDate } from 'Utility/date';
import { LeadDetails } from '../../../components';
import { isEmpty, isNil, path } from 'Utility/ramdaReplacement';
import { generatorsWithOneConnector, leadsWithOneConnector, leadsWithTwoConnectors } from '../../config';
import { getLatestGenerator } from 'Routes/Vns/utils';

const LeadEvents = ({ formData }: IOwnProps): React.JSX.Element => {
  return (
    <EventStepper
      name="leads"
      formData={formData}
      stepLabelText={(d: IVNSLead): string => formatPartialDate(d.date)}
      stepContent={(d: IVNSLead): React.JSX.Element => <LeadDetails lead={d} />}
      addNewTextHeader="vns.newLead"
      addNewTextButton="vns.newLead"
      saveDisabled={
        !(
          path(['leads', 0, 'leadType'], formData.document) ??
          path(['leads', 0, 'electrodeMountingLocation'], formData.document)
        )
      }
      // saveDisabledMessage={fm('vns.chooseDiscontinuationTypeFirst')}
      previousEventsTextHeader="vns.leads"
      editingElements={(index: number, onChange: IFormData['onChange']): React.JSX.Element => {
        const firstLead = Array.isArray(formData.document.leads) && formData.document.leads.length - 1 === index;
        return (
          <React.Fragment>
            <div style={{ marginBottom: '2rem' }}>
              <InputHandler
                type="PartialDate"
                editing={true}
                name="date"
                formData={{
                  onChange,
                  document: { date: formData.document.leads?.[index]?.date ?? '' },
                }}
                dateDefault={firstLead && formData.document.date ? formData.document.date : 'now'}
                isNotCancellable={true}
                dateHook={{
                  dateHookFloor: formData.document.date,
                }}
              />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ color: colors.darkGray, marginBottom: '0.5rem' }}>
                {fm('vns.electrodeMountingLocation')}
              </div>
              <InputHandler
                type="Select"
                formData={{
                  onChange,
                  document: {
                    electrodeMountingLocation: formData.document.leads?.[index]?.electrodeMountingLocation ?? '',
                  },
                }}
                editing={true}
                name="electrodeMountingLocation"
                options={['leftVagusNerve', 'rightVagusNerve', 'unknown']}
                placeholder="vns.chooseMountingLocation"
                optionFormatter={(name: string | number): React.JSX.Element =>
                  fm(`vns.opts.electrodeMountingLocation.${name}`)
                }
              />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ color: colors.darkGray, marginBottom: '0.5rem' }}>{fm('vns.leadType')}</div>
              <InputHandler
                type="SelectByCategory"
                formData={{
                  onChange,
                  document: { leadType: formData.document.leads?.[index]?.leadType ?? '' },
                }}
                editing={true}
                name="leadType"
                options={
                  isNil(formData.document.generators) ||
                  isEmpty(formData.document.generators) ||
                  (formData.document.generators.length === 1 && !formData.document.generators[0]?.generator)
                    ? [
                        { title: 'vns.leadsWithOneConnector', values: leadsWithOneConnector },
                        { title: 'vns.leadsWithTwoConnectors', values: leadsWithTwoConnectors },
                      ]
                    : generatorsWithOneConnector.includes(getLatestGenerator(formData.document)?.generator ?? '-')
                      ? [{ title: 'vns.leadsWithOneConnector', values: leadsWithOneConnector }]
                      : [{ title: 'vns.leadsWithTwoConnectors', values: leadsWithTwoConnectors }]
                }
                optionFormatter={(name: string | number) =>
                  ['unspecifiedLeadWithOneConnector', 'unspecifiedLeadWithTwoConnectors'].includes(name.toString())
                    ? fm(`vns.opts.leadType.${name}`)
                    : name.toString()
                }
                placeholder="vns.chooseLeadType"
                fullWidth={true}
                formatTitle={true}
              />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ color: colors.darkGray, marginBottom: '0.5rem' }}>{fm('vns.leadSerialNo')}</div>
              <InputHandler
                type="TextField"
                editing={true}
                name="leadSerialNo"
                formData={{
                  onChange,
                  document: { leadSerialNo: formData.document.leads?.[index]?.leadSerialNo ?? '' },
                }}
                placeholder="vns.enterSerialNo"
              />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ color: colors.darkGray, marginBottom: '0.5rem' }}>{fm('vns.leadUDIDICode')}</div>
              <InputHandler
                type="TextField"
                editing={true}
                name="leadUDIDICode"
                formData={{
                  onChange,
                  document: { leadUDIDICode: formData.document.leads?.[index]?.leadUDIDICode ?? '' },
                }}
                placeholder="vns.leadUDIDICode"
              />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ color: colors.darkGray, marginBottom: '0.5rem' }}>{fm('vns.implantationSite')}</div>
              <InputHandler
                type="Select"
                editing={true}
                name="implantationSite"
                options={['HYKS', 'TYKS', 'KYS', 'TAYS', 'OYS']}
                formData={{
                  onChange,
                  document: { implantationSite: formData.document.leads?.[index]?.implantationSite ?? '' },
                }}
                placeholder="vns.chooseImplantationSite"
              />
            </div>
          </React.Fragment>
        );
      }}
    />
  );
};

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'vns'>>;
  disabled?: boolean;
}

export default LeadEvents;
